/**
 * Bulk upload: the modal that turns a customer's spreadsheet (CSV, XLS or XLSX) of part numbers
 * and quantities into lines in the cart, a shopping list or a draft quote. This file owns the
 * state, the file parsing and the API calls; `BulkUploadModalDesktop.tsx` renders the steps.
 *
 * Steps (`bulkUploadStep`, held by the caller):
 * 1. Select a file (`FileSelection.tsx`). `readFile` parses the first sheet.
 * 2. Map the fields (`MapFields.tsx`): each Ryerson field in `defaultFileData` is paired with a
 *    column of the file. Signed-in users get their saved mapping pre-applied and can save a new
 *    one.
 * 3. Processing (`ProcessingFile.tsx`): a 2 s spinner that then calls `onSubmit`, which posts
 *    the rows for validation. The stepper is hidden on this step.
 * 4. Review (`UploadValidation.tsx`): rows the API accepted, and rows it rejected with an inline
 *    editor and "Process Corrections & Try Again". The button hands the valid rows to
 *    `onClickBulkAdd`.
 *
 * Position: rendered by `ShoppingCart/ShoppingCart.tsx` (`BulkUploadType.shoppingCart`),
 * `ShoppingListDetails/ShoppingListDetails.tsx` (`shoppingList`) and
 * `QuoteDetails/QuoteDetails.tsx` (`quote`). The modal adds nothing itself: each caller's
 * `onClickBulkAdd` builds its own request and closes the modal. It renders only at the `lg`
 * breakpoint and up. Bulk upload is desktop-only by design.
 *
 * Calls (all through the `axiosInstance` from `useApplication()`):
 * - `GET` / `POST APIUrl.BULK_UPLOAD_FIELD_MAPPING` (`web-users-api`
 *   `protected/bulk-upload/field-mappings`): the saved column mapping. Signed-in only; the route is
 *   protected. A failure is logged and the flow carries on without it.
 * - `POST APIUrl.BULK_UPLOAD` (`web-items-api` `public/validate-item-part-numbers`): validates the
 *   rows, here for corrections and in `BulkUploadHelpers.tsx` → `validateFileMapping` for the
 *   first pass. The zip code comes from `AccountSettings/Helpers/zipCode.ts` → `getZipCode`; for a
 *   guest or basic user the Items API resolves parts against the guest account of the sales plant
 *   serving that zip code, and fails the request without one.
 *
 * Rules a change must keep:
 * - Field keys differ between this UI and the Users API: `lineLevelPurchaseOrderNumber` is saved
 *   as `lineLevelPurchaseOrder` and `requestedDeliveryDate` as `futureDeliveryDate`
 *   (`FieldMappingResponse`, `web-users-api` `UserApi.BulkUploadFieldMappings`). The auto-match
 *   effect and `handleSaveMapping` translate in both directions and change together.
 * - Rows handed to `onClickBulkAdd` carry display UOMs from `DefaultUOMDisplay` (`lbs`, `pc(s)`,
 *   `sqft`), not API codes. The cart converts them with `Helper/shoppingCartHelper.ts` →
 *   `ValidRows_intoCartItems` (`formatUOMForResponse`). The list only upper-cases them, sending
 *   `LBS` / `PC(S)`. The quote upper-cases them and maps `PC(S)` → `PC`.
 * - On step 4 the submit button stays disabled after the first click, so one click hands the rows
 *   over once. It is re-enabled only by a step change or a new file.
 *
 * Related: `BulkUploadHelpers.tsx` (request mapping, error mapping, the corrections table cells),
 * `BulkUpload.model.ts` (request/response types), and the specs in
 * `unit-tests/shared/BulkUploadModal/`.
 */
import React, { useEffect, useRef, useState } from "react";
import { AxiosResponse, AxiosError } from "axios";
import { Media } from "@ryerson/frontend.next";
import { IError } from "@ryerson/common.models";
import BulkUploadModalDesktop from "./BulkUploadModalDesktop";
import { BulkUploadModalContent } from "./BulkUploadModalContent";
import { useApplication } from "@ryerson/frontend.application";
import { StepperDetail } from "@ryerson/frontend.progress";
import { MapFieldsContent } from "./MapFieldsContent";
import { CellObject, read, utils, WSKeys } from "xlsx";
import { APIUrl } from "@enums/apiUrl.enums";
import { isAxiosError } from "@components/Helper/TypeHelpers";
import { useEcommerce } from "@ryerson/frontend.e-commerce";
import { UploadValidationContent } from "./UploadValidationContent";
import { getZipCode } from "@components/AccountSettings/Helpers/zipCode";
import { dateToString, parseDateSafely } from "@components/Helper/common";
import {
	mapErrorMessage,
	mapErrorName,
	changeToUseGenericColumnNames,
	validateFileMapping,
} from "./BulkUploadHelpers";

import {
	BulkUploadValidationResponse,
	BulkUploadValidationRequest,
	PartValidation,
	PartValidationRes,
	BulkUploadType,
	FileMapping,
	FieldMappingResponse,
	WorkBookJson,
} from "./BulkUpload.model";
import { DefaultUOMDisplay } from "@ryerson/common.uom";

/**
 * Props of {@link BulkUploadModal}. The open flag, the step and the selected file live in
 * `ShoppingCartContext`, `ShoppingListDetailsContext` and `QuoteDetailsContext`. The desktop
 * subheaders open the modal through `setShowBulkUpload`; the modal resets the step and the files
 * itself when it closes.
 */
export type BulkUploadModalProps = {
	testingId?: string;
	showBulkUpload: boolean;
	setShowBulkUpload: React.Dispatch<React.SetStateAction<boolean>>;
	/** 1 select file, 2 map fields, 3 processing, 4 review. Reset to 1 when the modal closes. */
	bulkUploadStep: number;
	setBulkUploadStep: React.Dispatch<React.SetStateAction<number>>;
	/** At most one file (`FileSelection.tsx` sets `maxFiles={1}`); only `[0]` is read. */
	bulkUploadFiles: File[];
	setBulkUploadFiles: React.Dispatch<React.SetStateAction<File[]>>;
	/** Destination; decides whether the delivery-date field is offered and the button wording. */
	bulkUploadType: BulkUploadType;
	/**
	 * Receives the rows the Items API accepted when the customer confirms step 4. It must add them
	 * and close the modal; the modal leaves its button disabled after calling it.
	 */
	onClickBulkAdd: (validRows: PartValidationRes[]) => void;
};

/**
 * The bulk-upload modal; see the file header for the steps, the calls and the rules.
 */
const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
	testingId,
	showBulkUpload,
	setShowBulkUpload,
	bulkUploadStep,
	setBulkUploadStep,
	bulkUploadFiles,
	setBulkUploadFiles,
	onClickBulkAdd,
	bulkUploadType,
}) => {
	const {
		localization: { language },
		axiosInstance,
		user: { isLoggedIn },
		zipCode,
	} = useApplication();

	const content = BulkUploadModalContent[language || "en"];
	const mapFieldsContent = MapFieldsContent[language || "en"];
	const uploadValidationContent = UploadValidationContent[language || "en"];
	// The Ryerson fields offered on the map-fields step. `value` is the key in the validation
	// request (`PartValidation`); `ryersonColumn` is the translated label, which is also the text
	// the auto-match effect looks for in the file's headers when nothing is saved. The three
	// `required` fields are the ones `web-items-api` `PartNumberRequestValidator` and
	// `web-users-api` `BulkUploadFieldMappingsValidator` require.
	const defaultFileData: FileMapping[] = [
		{
			id: 1,
			userColumn: "",
			ryersonColumn: mapFieldsContent.partNumber,
			value: "partNumber",
			required: true,
		},
		{
			id: 2,
			userColumn: "",
			ryersonColumn: mapFieldsContent.quantity,
			value: "quantity",
			required: true,
		},
		{
			id: 3,
			userColumn: "",
			ryersonColumn: mapFieldsContent.quantityUom,
			value: "quantityUom",
			required: true,
		},
		{
			id: 4,
			userColumn: "",
			ryersonColumn: mapFieldsContent.pricingUom,
			value: "pricingUom",
			required: false,
		},
		{
			id: 5,
			userColumn: "",
			ryersonColumn: mapFieldsContent.customerRefNumber,
			value: "customerReferenceNumber",
			required: false,
		},
		{
			id: 6,
			userColumn: "",
			ryersonColumn: mapFieldsContent.lineLevelPO,
			value: "lineLevelPurchaseOrderNumber",
			required: false,
		},
		{
			id: 7,
			userColumn: "",
			ryersonColumn: mapFieldsContent.lineItemNotes,
			value: "lineItemNotes",
			required: false,
		},
		// Cart and quotes only: the shopping-list add (`ShoppingListDetails.tsx` →
		// `bulkUploadAddItemsToShoppingList`) sends no delivery date.
		...(bulkUploadType === BulkUploadType.shoppingCart ||
		bulkUploadType === BulkUploadType.quote
			? [
					{
						id: 8,
						userColumn: "",
						ryersonColumn: mapFieldsContent.deliveryDate,
						value: "requestedDeliveryDate",
						required: false,
					},
			  ]
			: []),
	];

	const [fileContents, setFileContents] = useState<{ [key: string]: string | number }[]>([{}]);
	const [fileMapping, setFileMapping] = useState<FileMapping[]>([...defaultFileData]);
	const [isMissingRequiredData, setIsMissingRequiredData] = useState<boolean>(false);
	const [submittedFileInput, setSubmittedFileInput] = useState<boolean>(false);
	const [validFile, setValidFile] = useState<boolean>(true);
	const [disabledSubmitButton, setDisabledSubmitButton] = useState<boolean>(
		bulkUploadStep === 1 ? bulkUploadFiles.length === 0 || !validFile : false
	);
	const [displayCancelUpload, setDisplayCancelUpload] = useState<boolean>(false);
	const [validatedData, setValidatedData] = useState<PartValidationRes[]>([]);
	const [invalidData, setInvalidData] = useState(
		validatedData.filter((rowData) => !rowData.valid)
	);
	const invalidDataRef = useRef<PartValidationRes[]>([]);
	const [isGenericError, setIsGenericError] = useState<boolean>(false);
	const [saveMapping, setSaveMapping] = useState<boolean>(false);
	const [userSavedMapping, setUserSavedMapping] = useState<FieldMappingResponse>({});

	// Re-derives the rows awaiting correction whenever the validated set changes. A row the
	// customer has edited lives only in `invalidDataRef` (the editors in `BulkUploadHelpers.tsx`
	// write there without re-rendering), so that copy wins over the server's copy.
	useEffect(() => {
		const newLocal = [...validatedData.filter((rowData) => !rowData.valid)].map(
			(validatedRow) =>
				invalidDataRef.current.find(
					(invalidRow) => invalidRow.id === validatedRow.id && invalidRow.updatedByUser
				) ?? validatedRow
		);
		invalidDataRef.current = newLocal;
		setInvalidData(newLocal);
	}, [validatedData]);

	const [processCorrections, setProcessCorrections] = useState<boolean>(false);

	const { selectedShipToAddress, customerPickup, salesPlant } = useEcommerce();

	// Submit-button label per step (`bulkUploadStep - 1`). On step 3 the button reads "Cancel" and
	// opens the cancel confirmation (`BulkUploadModalDesktop.tsx` → `getModalButtons`). The plural
	// "s" on the item word is appended in every language.
	const buttonTextArray = [
		content.upload,
		content.mapFields,
		content.cancel,
		content.addItemsToShoppingCart
			.replace(
				/%validatedItems%/,
				validatedData.filter((rowData) => rowData.valid).length.toString()
			)
			.replace(
				/%Items%/,
				content.item +
					(validatedData.filter((rowData) => rowData.valid).length > 1 ? "s" : "")
			)
			.replace(
				/%ShoppingCart%/,
				bulkUploadType === BulkUploadType.shoppingCart
					? content.shoppingCart
					: bulkUploadType === BulkUploadType.quote
					? content.quote
					: content.shoppingList
			),
	];

	// Stepper shown on steps 1, 2 and 4. `isActive` marks a step as passed; step 3 (processing) has
	// no stepper entry, and "Review Items" is never marked passed because the flow ends on step 4.
	let steps: StepperDetail[] = [
		{
			title: content.selectFile,
			indicator: "1",
			isActive: bulkUploadStep > 1,
		},
		{
			title: content.mapFields,
			indicator: "2",
			isActive: bulkUploadStep > 2,
		},
		{
			title: content.reviewItems,
			indicator: "3",
			isActive: bulkUploadStep > 4,
		},
	];

	/**
	 * Leaves step 1. A signed-in user's saved mapping is loaded first, so the auto-match effect can
	 * use it; a guest skips the call because the route is protected. Any failure is logged and the
	 * flow moves to step 2 regardless.
	 */
	const bulkUploadStepHandler = async () => {
		if (isLoggedIn) {
			await axiosInstance
				.get<null, AxiosResponse<FieldMappingResponse> | AxiosError<IError>>(
					`${APIUrl.BULK_UPLOAD_FIELD_MAPPING}`
				)
				.then((response) => {
					if (isAxiosError(response) || response.status !== 200) {
						throw response;
					}
					const { data } = response;
					if (data) {
						setUserSavedMapping(data);
					}
				})
				.catch((err) => {
					console.error("get saved mapping error", err);
				})
				.finally(() => {
					setBulkUploadStep(2);
				});
		} else {
			setBulkUploadStep(2);
		}
	};

	useEffect(() => {
		setSubmittedFileInput(false);
	}, [bulkUploadStep]);

	useEffect(() => {
		if (bulkUploadFiles.length) {
			readFile(bulkUploadFiles[0]);
		} else {
			setValidFile(true);
		}
	}, [bulkUploadFiles]);

	useEffect(() => {
		setDisabledSubmitButton(
			bulkUploadStep === 1 ? bulkUploadFiles.length === 0 || !validFile : false
		);
	}, [bulkUploadStep, bulkUploadFiles, validFile]);

	// Auto-match: for each Ryerson field, look for the saved column name (or, with none saved, the
	// field's translated label) in the file's headers. The first header that contains it, ignoring
	// case, is selected; a field with no match keeps its current selection. The mapping objects
	// are updated in place.
	useEffect(() => {
		const newMapping = [...fileMapping].map((mapping) => {
			const updatedSavedMapping = {
				...userSavedMapping,
				lineLevelPurchaseOrderNumber: userSavedMapping.lineLevelPurchaseOrder,
				requestedDeliveryDate: userSavedMapping.futureDeliveryDate,
			};
			const columnName =
				updatedSavedMapping[mapping.value as keyof FieldMappingResponse] ||
				mapping.ryersonColumn;
			const matchingUserColumn: string | undefined = Object.keys(fileContents[0]).find(
				(userColumn: string) => userColumn.toLowerCase().includes(columnName.toLowerCase())
			);
			if (matchingUserColumn) {
				mapping.userColumn = matchingUserColumn;
			}
			return mapping;
		});
		setFileMapping(newMapping);
	}, [fileContents, userSavedMapping]);

	// Closing the modal discards the file and every result. `saveMapping` and `isGenericError` are
	// not reset here.
	useEffect(() => {
		if (!showBulkUpload) {
			setBulkUploadStep(1);
			setSubmittedFileInput(false);
			setBulkUploadFiles([]);
			setIsMissingRequiredData(false);
			setFileContents([{}]);
			setValidFile(false);
			setDisplayCancelUpload(false);
			setValidatedData([]);
			setProcessCorrections(false);
		} else {
			setFileMapping([...defaultFileData]);
		}
	}, [showBulkUpload]);

	/**
	 * Saves the current mapping as `{ <Users API field>: <file column header> }`, renaming the two
	 * fields whose keys differ (see the file header). Fire-and-forget: called when leaving step 2
	 * with "save this mapping" ticked; a failure is only logged and the upload continues.
	 */
	const handleSaveMapping = () => {
		const requestBody = fileMapping.reduce((acc, cur) => {
			let columnValue = cur.value;
			if (columnValue === "lineLevelPurchaseOrderNumber") {
				columnValue = "lineLevelPurchaseOrder";
			}
			if (columnValue === "requestedDeliveryDate") {
				columnValue = "futureDeliveryDate";
			}
			return { ...acc, [columnValue]: cur.userColumn };
		}, {});

		axiosInstance
			.post<FieldMappingResponse, AxiosResponse | AxiosError<IError>>(
				`${APIUrl.BULK_UPLOAD_FIELD_MAPPING}`,
				requestBody
			)
			.then((response) => {
				if (isAxiosError(response) || response.status !== 200) {
					throw response;
				}
			})
			.catch((err) => {
				console.error("post save mapping error", err);
			})
			.finally(() => {
				setSaveMapping(false);
			});
	};

	/**
	 * Advances the flow from the current step: 1 → load the saved mapping and go to 2; 2 → check
	 * the required fields are mapped, save the mapping if asked, go to 3; 3 (called by
	 * `ProcessingFile.tsx`) → validate the rows, then 4 on success or back to 2 with a generic
	 * error; 4 → hand the valid rows to `onClickBulkAdd`.
	 */
	const onSubmit = () => {
		if (isGenericError) {
			setIsGenericError(false);
		}
		setSubmittedFileInput(true);
		if (bulkUploadStep === 1 && bulkUploadFiles.length > 0 && validFile) {
			bulkUploadStepHandler();
		} else if (bulkUploadStep === 2) {
			if (fileMapping.some((field) => field.required && !field.userColumn)) {
				setIsMissingRequiredData(true);
			} else {
				setIsMissingRequiredData(false);
				setBulkUploadStep(3);
				saveMapping && handleSaveMapping();
				// what to do on fail?
			}
		} else if (bulkUploadStep === 3) {
			// Prepares the API's rows for the review step: UOM codes become display values
			// (`undefined` for a code `DefaultUOMDisplay` lacks), the API's error field and message
			// are mapped to this UI's keys and translated text, and the delivery date is normalised
			// to `YYYY-MM-DD`.
			const onSuccessBulkValidate = ({ data }: { data: BulkUploadValidationResponse }) => {
				const updatedParts: PartValidationRes[] = data.parts.map((part) => {
					return {
						...part,
						quantityUom:
							DefaultUOMDisplay[
								part.quantityUom?.toUpperCase() as keyof typeof DefaultUOMDisplay
							],
						pricingUom:
							DefaultUOMDisplay[
								part.pricingUom?.toUpperCase() as keyof typeof DefaultUOMDisplay
							],
						errorField: mapErrorName(part),
						errorMessage: mapErrorMessage(part, uploadValidationContent),
						updatedByUser: false,
						requestedDeliveryDate: (() => {
							if (!part.requestedDeliveryDate) return "";
							// Use timezone-safe date parsing and formatting to fix bulk upload timezone issues
							// `parseDateSafely` reads YYYY-MM-DD, MM/DD/YYYY, YYYY/MM/DD and ISO
							// text. Text with `T`, `/` or `-` whose parts are not numbers gives an
							// invalid date, which `dateToString` replaces with today. Text with
							// none of those gives `null`, and the field becomes `""`.
							const safeDate = parseDateSafely(part.requestedDeliveryDate);
							return safeDate ? dateToString(safeDate) : "";
						})(),
					};
				});

				setValidatedData([...updatedParts]);
				setBulkUploadStep(4);
			};
			validateFileMapping(
				fileMapping,
				fileContents,
				language,
				axiosInstance,
				getZipCode(isLoggedIn, zipCode, selectedShipToAddress, customerPickup, salesPlant),
				onSuccessBulkValidate,
				() => {
					setIsGenericError(true);
					setBulkUploadStep(2);
				}
			);
		} else if (bulkUploadStep === 4) {
			// Disabled before the hand-over so a second click cannot add the rows twice.
			setDisabledSubmitButton(true);
			onClickBulkAdd(validatedData.filter((rowData) => rowData.valid));
		}
	};

	// "Process Corrections & Try Again" (`UploadValidation.tsx` → `handleProcessCorrectionsClick`)
	// sets `processCorrections` and updates `validatedData`, which runs this effect. Only the rows
	// awaiting correction are re-validated, and the result replaces `invalidData`, not
	// `validatedData`: `UploadValidation.tsx` moves rows that came back valid into the validated
	// set 3 s later. A row the API still rejects gets its new error mapped as on the first pass;
	// a row with no error keeps its previous error field and message, and the table shows it as
	// resolved because `valid` is true. The request has no failure handler: on a rejection the
	// rows stay as they were.
	useEffect(() => {
		if (processCorrections) {
			const parts = invalidData.map(
				(row: PartValidationRes): PartValidation => ({
					id: row.id,
					partNumber: row.partNumber,
					// An edited quantity is a number (`updateInvalidData`); the Items API validator
					// fails the whole request with a 500 on a non-string field.
					quantity: row.quantity?.toString() || "",
					quantityUom: row.quantityUom?.toUpperCase(),
					pricingUom: row.pricingUom?.toUpperCase(),
					customerReferenceNumber: row.customerReferenceNumber,
					lineLevelPurchaseOrderNumber: row.lineLevelPurchaseOrderNumber,
					lineItemNotes: row.lineItemNotes,
					requestedDeliveryDate: row.requestedDeliveryDate,
				})
			);
			const requestBody: BulkUploadValidationRequest = {
				zipCode: getZipCode(
					isLoggedIn,
					zipCode,
					selectedShipToAddress,
					customerPickup,
					salesPlant
				),
				language,
				parts,
			};
			axiosInstance
				.post(`${APIUrl.BULK_UPLOAD}`, requestBody)
				.then(({ data }: { data: BulkUploadValidationResponse }) => {
					const updatedParts: PartValidationRes[] = data.parts.map((part) => {
						return {
							...part,
							quantityUom:
								DefaultUOMDisplay[
									part.quantityUom?.toUpperCase() as keyof typeof DefaultUOMDisplay
								],
							pricingUom:
								DefaultUOMDisplay[
									part.pricingUom?.toUpperCase() as keyof typeof DefaultUOMDisplay
								],
							updatedByUser: false,
							errorField: !!part.errorField
								? mapErrorName(part)
								: invalidData.find((invalidRow) => invalidRow.id === part.id)
										?.errorField || null,
							errorMessage: !!part.errorMessage
								? mapErrorMessage(part, uploadValidationContent)
								: invalidData.find((invalidRow) => invalidRow.id === part.id)
										?.errorMessage || null,
						};
					});
					invalidDataRef.current = [...updatedParts];
					setInvalidData([...updatedParts]);
				});
			setProcessCorrections(false);
		}
	}, [validatedData]);

	/**
	 * Applies a column choice on the map-fields step (`MapFieldsDataTableConfig.tsx` →
	 * `DropdownUserField`). Once the "Required" errors are showing, they are re-checked on every
	 * change so they clear as the customer fixes them.
	 */
	const handleDropdownSelection = (newValue: string, fileField: FileMapping) => {
		const updatedFileMapping = [...fileMapping].map((mapping) => {
			if (fileField.ryersonColumn === mapping.ryersonColumn) {
				mapping.userColumn = newValue;
			}
			return mapping;
		});
		setFileMapping(updatedFileMapping);
		if (isMissingRequiredData) {
			setIsMissingRequiredData(
				updatedFileMapping
					.filter((field) => field.required)
					.some((field) => !field.userColumn)
			);
		}
	};

	/**
	 * Parses the first sheet of the selected file into `fileContents`, one object per row keyed by
	 * the header row's text. `cellDates: true` makes date cells `Date` objects. A sheet with no
	 * data rows sets `validFile` to false ("No data found in file").
	 *
	 * this step is necessary in order to find the columns that do not contain any data in the spreadsheet:
	 * any empty columns (though they have a header) will not be parsed by {@link utils.sheet_to_json}
	 * so in order to have them as an option, it is necessary to read the fileHeaders separately and
	 * associate them in the JSON file contents with an empty string
	 */

	const readFile = (file: File) => {
		const reader = new FileReader();
		reader.readAsBinaryString(file);
		reader.onload = function (event) {
			const data = event?.target?.result;
			let workbook = read(data, { type: "binary", cellDates: true });

			// Header texts: the values of the cells in row 1 (cell keys such as `A1`, `AB1`). Keys
			// starting with `!` are sheet metadata (`!ref`, `!merges`).
			const fileHeaders = Object.entries(workbook.Sheets[workbook.SheetNames[0]])
				.filter(([key, value]: [string, CellObject | WSKeys | any]) => {
					return key[0] !== "!" && parseInt(key.replace(/[A-Z]*/, "")) === 1;
				})
				.map(([k, v]) => v.v);

			let rowObject: WorkBookJson = utils.sheet_to_json(
				workbook.Sheets[workbook.SheetNames[0]],
				{ defval: "" }
			);

			if (rowObject.length <= 0) {
				setValidFile(false);
			} else {
				rowObject.map((cur) => {
					fileHeaders?.forEach((key) => {
						if (!Object.keys(cur).includes(key)) {
							cur[key] = "";
						}
					});
					return cur;
				});

				// If Headers are not there , rename column keys as GENERIC names (Column A, Column B etc...)
				// Past 26 columns the names continue Column AA, Column AB, ... up to Column ZZ.
				if (fileHeaders.length === 0) {
					rowObject = changeToUseGenericColumnNames(rowObject);
				}

				setFileContents(rowObject);
				setValidFile(true);
			}

			return rowObject;
		};
	};

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<BulkUploadModalDesktop
					content={content}
					testingId={testingId}
					showBulkUpload={showBulkUpload}
					setShowBulkUpload={setShowBulkUpload}
					bulkUploadStep={bulkUploadStep}
					steps={steps}
					fileMapping={fileMapping}
					isMissingRequiredData={isMissingRequiredData}
					onSubmit={onSubmit}
					disabledSubmitButton={disabledSubmitButton}
					handleDropdownSelection={handleDropdownSelection}
					submittedFileInput={submittedFileInput}
					bulkUploadFiles={bulkUploadFiles}
					setBulkUploadFiles={setBulkUploadFiles}
					fileFields={Object.keys(fileContents[0])}
					buttonText={buttonTextArray[bulkUploadStep - 1]}
					validFile={validFile}
					validatedData={validatedData}
					setValidatedData={setValidatedData}
					processCorrections={processCorrections}
					setProcessCorrections={setProcessCorrections}
					displayCancelUpload={displayCancelUpload}
					setDisplayCancelUpload={setDisplayCancelUpload}
					invalidData={invalidData}
					setInvalidData={setInvalidData}
					invalidDataRef={invalidDataRef}
					bulkUploadType={bulkUploadType}
					isGenericError={isGenericError}
					saveMapping={saveMapping}
					setSaveMapping={setSaveMapping}
					isLoggedIn={isLoggedIn}
				/>
			</Media>
		</>
	);
};

export default BulkUploadModal;
